import type { ManiaEngineInput } from '@shared/engine/mania/core';
import type { ManiaLane } from '@shared/maniaNote';
import {
  KeyboardManiaInputSource,
  ReplayManiaInputSource,
  type ManiaInputSource,
} from '../mania/maniaInputSource';
import { laneFromClientX } from './guitarPlayfieldGeometry';

/**
 * Guitar-mode input source contract. Re-exports the shared
 * {@link ManiaInputSource} interface so existing call sites can use
 * the friendlier `GuitarInputSource` name without forcing the shared
 * base out of internal modules.
 */
export type GuitarInputSource = ManiaInputSource;

/**
 * Live input source for Guitar mode. Thin wrapper over
 * {@link KeyboardManiaInputSource} that wires the Guitar-specific
 * pointer geometry (perspective lanes via {@link laneFromClientX})
 * and keybind slice (`prefs.guitarLanes`, defaulting to D / F / J / K)
 * into the shared mania input implementation.
 */
export class KeyboardGuitarInputSource extends KeyboardManiaInputSource {
  constructor(
    canvas: HTMLCanvasElement,
    getBeatAtNow: () => number,
    isClockRunning: () => boolean = () => true,
  ) {
    super({
      mode: 'guitar',
      canvas,
      laneFromClientX,
      getBeatAtNow,
      isClockRunning,
    });
  }
}

/**
 * Replay input source for Guitar mode. Identical mechanics to the
 * shared {@link ReplayManiaInputSource}; kept as a named subclass so
 * Guitar session code can declare `ReplayGuitarInputSource` types
 * without leaking the shared base name through the public API.
 */
export class ReplayGuitarInputSource extends ReplayManiaInputSource {
  constructor(inputs: ManiaEngineInput[]) {
    super(inputs);
  }
}

export type { ManiaLane };
